import React from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {faICursor, type IconDefinition} from '@netx/core-icons';
import {ReactHookFormTextField} from '@netx/core-components/Form/TextField';
import {
	type ExtendFormDialogProps,
	FormDialog,
} from '@netx/core-components/FormDialog';
import {RenameFormDialogLabel, RenameFormDialogTitle} from './locales';

type RenameFormDialogFieldValues = Record<string, string>;

export interface RenameFormDialogProps<
	FV extends RenameFormDialogFieldValues = RenameFormDialogFieldValues,
> extends Omit<ExtendFormDialogProps, 'name'> {
	form: UseFormReturn<FV>;
	label?: string;
	name: keyof FV;
	icon?: IconDefinition;
	title?: string;
	secondaryTitle?: React.ReactNode;
}

export const RenameFormDialog = <
	FV extends RenameFormDialogFieldValues = RenameFormDialogFieldValues,
>({
	form,
	icon = faICursor,
	label,
	name,
	open,
	secondaryTitle,
	title,
	onClose,
	onSubmit,
	FormDialogProps,
	DialogProps,
	...props
}: RenameFormDialogProps<FV>) => {
	return (
		<FormDialog
			open={open}
			error={form.formState.errors.root?.message}
			isDirty={form.formState.isDirty}
			isValid={form.formState.isValid}
			isSubmitting={form.formState.isSubmitting}
			title={title ?? RenameFormDialogTitle.RENAME}
			confirmLabel={RenameFormDialogLabel.SAVE}
			{...FormDialogProps}
			onSubmit={(event) => {
				event.preventDefault();
				onSubmit?.(event);
			}}
			DialogProps={{
				fullScreenWhenMobile: true,
				fullWidth: true,
				icon: icon,
				maxWidth: 'xs',
				preventBackdropClose: true,
				secondaryTitle,
				...DialogProps,
			}}
			onClose={onClose}
		>
			<ReactHookFormTextField<FV>
				autoFocus
				required
				control={form.control}
				label={label ?? RenameFormDialogLabel.NAME}
				name={name as string}
			/>
		</FormDialog>
	);
};
